import React, { useEffect, useState } from 'react';
import { Modal } from '@consta/uikit/Modal';
import { TextField } from '@consta/uikit/TextField';
import { Combobox } from '@consta/uikit/Combobox';
import { Button } from '@consta/uikit/Button';
import { Field, Deposit, Section, Horizon, Bed } from '../types/types';
import { usePostFieldMutation, useLazyGetFieldUuidQuery, usePatchFieldMutation, useLazyGetFieldTypeQuery } from '../../../../services/fieldService';
import { usePostDepositMutation, useLazyGetDepositUuidQuery, usePatchDepositMutation, useLazyGetDepositTypeQuery } from '../../../../services/depositService';
import { usePostAreaMutation, useLazyGetAreaUuidQuery, usePatchAreaMutation } from '../../../../services/areaService';
import { usePostHorizonMutation, useLazyGetHorizonUuidQuery, usePatchHorizonMutation } from '../../../../services/horizonService';
import { usePostLayerMutation, useLazyGetLayerUuidQuery, usePatchLayerMutation } from '../../../../services/layerService';
import { useLazyGetCompanyQuery } from '../../../../services/companyService';
import { useLazyGetReservoirQuery } from '../../../../services/reservoirService';
import { useLazyGetLookupsQuery } from '../../../../services/lookupsService';
import classes from './style/FieldModals.module.scss'

interface FieldModalsProps {
    fieldModal: { isOpen: boolean; field?: Field | null };
    setFieldModal: (modal: { isOpen: boolean; field?: Field | null }) => void;
    depositModal: { isOpen: boolean; fieldId: string; deposit?: Deposit | null };
    setDepositModal: (modal: { isOpen: boolean; fieldId: string; deposit?: Deposit | null }) => void;
    sectionModal: { isOpen: boolean; fieldId: string; depositId: string; section?: Section | null };
    setSectionModal: (modal: { isOpen: boolean; fieldId: string; depositId: string; section?: Section | null }) => void;
    horizonModal: { isOpen: boolean; fieldId: string; depositId: string; sectionId: string; horizon?: Horizon | null };
    setHorizonModal: (modal: { isOpen: boolean; fieldId: string; depositId: string; sectionId: string; horizon?: Horizon | null }) => void;
    bedModal: { isOpen: boolean; fieldId: string; depositId: string; sectionId: string; horizonId: string; bed?: Bed | null };
    setBedModal: (modal: { isOpen: boolean; fieldId: string; depositId: string; sectionId: string; horizonId: string; bed?: Bed | null }) => void;
    localField: Field | null | any;
    setLocalField: (field: Field | null | any) => void;
    localDeposit: Deposit | null | any;
    setLocalDeposit: (deposit: Deposit | null | any) => void;
    localSection: Section | null | any;
    setLocalSection: (section: Section | null | any) => void;
    localHorizon: Horizon | null | any
    setLocalHorizon: (horizon: Horizon | null | any) => void;
    localBed: Bed | null | any;
    setLocalBed: (bed: Bed | null | any) => void;
    onSaveField: () => void;
    onSaveDeposit: () => void;
    onSaveSection: () => void;
    onSaveHorizon: () => void;
    onSaveBed: () => void;
}

const FieldModals: React.FC<FieldModalsProps> = ({
    fieldModal,
    setFieldModal,
    depositModal,
    setDepositModal,
    sectionModal,
    setSectionModal,
    horizonModal,
    setHorizonModal,
    bedModal,
    setBedModal,
    localField,
    setLocalField,
    localDeposit,
    setLocalDeposit,
    localSection,
    setLocalSection,
    localHorizon,
    setLocalHorizon,
    localBed,
    setLocalBed,
    onSaveField,
    onSaveDeposit,
    onSaveSection,
    onSaveHorizon,
    onSaveBed
}) => {
    type ItemsStage =
        {
            id: number,
            label: any
        }

    const itemsStageCombobox: ItemsStage[] = [
        {
            id: 1,
            label: 1
        },
        {
            id: 2,
            label: 2
        },
        {
            id: 3,
            label: 3
        },
        {
            id: 4,
            label: 4
        },
        {
            id: 5,
            label: 'Нет данных'
        },
    ]

    // Состояния для справочных данных
    const [fieldNames, setFieldNames] = useState<any[]>([]);
    const [depositNames, setDepositNames] = useState<any[]>([]);
    const [areaNames, setAreaNames] = useState<any[]>([]);
    const [horizonCodes, setHorizonCodes] = useState<any[]>([]);
    const [codesHorizon, setCodesHorizon] = useState<any[]>([]);
    const [layerNames, setLayerNames] = useState<any[]>([]);
    const [areasName, setAreasName] = useState<any>('')

    // Мутации для создания сущностей
    const [postField, { isLoading: isCreatingField }] = usePostFieldMutation();
    const [patchField, { isLoading: isUpdatingField }] = usePatchFieldMutation();
    const [postDeposit, { isLoading: isCreatingDeposit }] = usePostDepositMutation();
    const [patchDeposit, { isLoading: isUpdatingDeposit }] = usePatchDepositMutation();
    const [postArea, { isLoading: isCreatingArea }] = usePostAreaMutation();
    const [patchArea, { isLoading: isUpdatingArea }] = usePatchAreaMutation();
    const [postHorizon, { isLoading: isCreatingHorizon }] = usePostHorizonMutation();
    const [patchHorizon, { isLoading: isUpdatingHorizon }] = usePatchHorizonMutation();
    const [postLayer, { isLoading: isCreatingLayer }] = usePostLayerMutation();
    const [patchLayer, { isLoading: isUpdatingLayer }] = usePatchLayerMutation();

    // Lazy запросы для получения данных по UUID
    const [getFieldUuid, { data: fieldUuidData, isLoading: isLoadingFieldUuid }] = useLazyGetFieldUuidQuery();
    const [getDepositUuid, { data: depositUuidData, isLoading: isLoadingDepositUuid }] = useLazyGetDepositUuidQuery();
    const [getAreaUuid, { data: areaUuidData, isLoading: isLoadingAreaUuid }] = useLazyGetAreaUuidQuery();
    const [getHorizonUuid, { data: horizonUuidData, isLoading: isLoadingHorizonUuid }] = useLazyGetHorizonUuidQuery();
    const [getLayerUuid, { data: layerUuidData, isLoading: isLoadingLayerUuid }] = useLazyGetLayerUuidQuery();

    // Lazy запросы для получения справочных данных
    const [getCompanies, { data: companiesData, isLoading: isLoadingCompanies }] = useLazyGetCompanyQuery();
    const [getReservoirTypes, { data: reservoirTypesData, isLoading: isLoadingReservoirTypes }] = useLazyGetReservoirQuery();
    const [getFieldTypes, { data: fieldTypesData, isLoading: isLoadingFieldTypes }] = useLazyGetFieldTypeQuery();
    const [getDepositTypes, { data: depositTypesData, isLoading: isLoadingDepositTypes }] = useLazyGetDepositTypeQuery();

    // Lazy запросы для получения справочных значений из lookups
    const [getLookups, { isLoading: isLoadingLookups }] = useLazyGetLookupsQuery();

    // Загрузка данных при открытии модальных окон редактирования
    useEffect(() => {
        if (fieldModal.isOpen && fieldModal.field?.uuid) {
            getFieldUuid({ uuid: fieldModal.field.uuid });
        }
    }, [fieldModal.isOpen, fieldModal.field?.uuid, getFieldUuid]);

    useEffect(() => {
        if (depositModal.isOpen && depositModal.deposit?.uuid) {
            getDepositUuid({ uuid: depositModal.deposit.uuid });
        }
    }, [depositModal.isOpen, depositModal.deposit?.uuid, getDepositUuid]);

    useEffect(() => {
        if (sectionModal.isOpen && sectionModal.section?.uuid) {
            getAreaUuid({ uuid: sectionModal.section.uuid });
        }
        setAreasName(areaUuidData?.name)
    }, [sectionModal.isOpen, sectionModal.section?.uuid, getAreaUuid]);

    useEffect(() => {
        if (horizonModal.isOpen && horizonModal.horizon?.uuid) {
            getHorizonUuid({ uuid: horizonModal.horizon.uuid });
        }
    }, [horizonModal.isOpen, horizonModal.horizon?.uuid, getHorizonUuid]);

    useEffect(() => {
        if (bedModal.isOpen && bedModal.bed?.uuid) {
            getLayerUuid({ uuid: bedModal.bed.uuid });
        }
    }, [bedModal.isOpen, bedModal.bed?.uuid, getLayerUuid]);

    // Загрузка данных из lookups
    useEffect(() => {
        const loadLookupsData = async () => {
            if (fieldModal.isOpen) {
                try {
                    const names: any = await getLookups({ type: 'field', limit: 1000 }).unwrap();
                    // Проверяем структуру данных
                    if (Array.isArray(names)) {
                        setFieldNames(names);
                    } else if (names?.results && Array.isArray(names.results)) {
                        setFieldNames(names.results);
                    } else if (names?.data && Array.isArray(names.data)) {
                        setFieldNames(names.data);
                    } else {
                        setFieldNames([]);
                    }
                } catch (error) {
                    console.error('Ошибка загрузки названий месторождений:', error);
                    setFieldNames([]);
                }
            }
        };
        loadLookupsData();
    }, [fieldModal.isOpen, getLookups]);

    useEffect(() => {
        const loadLookupsData = async () => {
            if (depositModal.isOpen) {
                try {
                    const names: any = await getLookups({ type: 'deposit', limit: 1000 }).unwrap();
                    if (Array.isArray(names)) {
                        setDepositNames(names);
                    } else if (names?.results && Array.isArray(names.results)) {
                        setDepositNames(names.results);
                    } else if (names?.data && Array.isArray(names.data)) {
                        setDepositNames(names.data);
                    } else {
                        setDepositNames([]);
                    }
                } catch (error) {
                    console.error('Ошибка загрузки названий залежей:', error);
                    setDepositNames([]);
                }
            }
        };
        loadLookupsData();
    }, [depositModal.isOpen, getLookups]);

    // useEffect(() => {
    //     const loadLookupsData = async () => {
    //         if (sectionModal.isOpen) {
    //             try {
    //                 const names: any = await getLookups({ type: 'area', limit: 1000 }).unwrap();
    //                 if (Array.isArray(names)) {
    //                     setAreaNames(names);
    //                 } else if (names?.results && Array.isArray(names.results)) {
    //                     setAreaNames(names.results);
    //                 } else if (names?.data && Array.isArray(names.data)) {
    //                     setAreaNames(names.data);
    //                 } else {
    //                     setAreaNames([]);
    //                 }
    //             } catch (error) {
    //                 console.error('Ошибка загрузки названий участков:', error);
    //                 setAreaNames([]);
    //             }
    //         }
    //     };
    //     loadLookupsData();
    // }, [sectionModal.isOpen, getLookups]);

    useEffect(() => {
        const loadLookupsData = async () => {
            if (horizonModal.isOpen) {
                try {
                    const codes: any = await getLookups({ type: 'horizon', limit: 1000 }).unwrap();
                    const horizonCodes: any = await getLookups({ type: 'code', limit: 1000 }).unwrap();
                    if (Array.isArray(codes)) {
                        setHorizonCodes(codes);
                    } else if (codes?.results && Array.isArray(codes.results)) {
                        setHorizonCodes(codes.results);
                    } else if (codes?.data && Array.isArray(codes.data)) {
                        setHorizonCodes(codes.data);
                    } else {
                        setHorizonCodes([]);
                    }
                    if (Array.isArray(horizonCodes)) {
                        setCodesHorizon(horizonCodes);
                    } else if (horizonCodes?.results && Array.isArray(horizonCodes.results)) {
                        setCodesHorizon(horizonCodes.results);
                    } else if (horizonCodes?.data && Array.isArray(horizonCodes.data)) {
                        setCodesHorizon(horizonCodes.data);
                    } else {
                        setCodesHorizon([]);
                    }
                } catch (error) {
                    console.error('Ошибка загрузки кодов горизонтов:', error);
                    setHorizonCodes([]);
                    setCodesHorizon([]);
                }
            }
        };
        loadLookupsData();
    }, [horizonModal.isOpen, getLookups]);

    useEffect(() => {
        const loadLookupsData = async () => {
            if (bedModal.isOpen) {
                try {
                    const names: any = await getLookups({ type: 'layer', limit: 1000 }).unwrap();
                    if (Array.isArray(names)) {
                        setLayerNames(names);
                    } else if (names?.results && Array.isArray(names.results)) {
                        setLayerNames(names.results);
                    } else if (names?.data && Array.isArray(names.data)) {
                        setLayerNames(names.data);
                    } else {
                        setLayerNames([]);
                    }
                } catch (error) {
                    console.error('Ошибка загрузки названий пластов:', error);
                    setLayerNames([]);
                }
            }
        };
        loadLookupsData();
    }, [bedModal.isOpen, getLookups]);

    // Получение справочных данных с параметрами
    useEffect(() => {
        if (fieldModal.isOpen || depositModal.isOpen || sectionModal.isOpen) {
            getCompanies({
                limit: 100,
                offset: 0,
                ordering: 'name'
            });
        }
    }, [fieldModal.isOpen, depositModal.isOpen, sectionModal.isOpen, getCompanies]);

    useEffect(() => {
        if (bedModal.isOpen) {
            getReservoirTypes({
                limit: 100,
                offset: 0
            });
        }
    }, [bedModal.isOpen, getReservoirTypes]);

    useEffect(() => {
        if (fieldModal.isOpen) {
            getFieldTypes({
                limit: 100,
                offset: 0
            });
        }
    }, [fieldModal.isOpen, getFieldTypes]);

    useEffect(() => {
        if (depositModal.isOpen) {
            getDepositTypes({
                limit: 100,
                offset: 0
            });
        }
    }, [depositModal.isOpen, getDepositTypes]);

    // Функции для преобразования данных в формат Combobox
    const getCompaniesForCombobox = () => {
        const companies = companiesData?.results || [];
        const items = companies.map(company => ({
            id: company.uuid,
            value: company.uuid,
            label: company.name
        }));
        return items;
    };

    const getFieldTypesForCombobox = () => {
        let types: any = [];
        if (fieldTypesData?.results) {
            types = fieldTypesData.results;
        } else if (Array.isArray(fieldTypesData)) {
            types = fieldTypesData;
        } else if (fieldTypesData?.data) {
            types = fieldTypesData.data;
        }
        const items = types.map((type: any) => ({
            id: type.uuid || type.id,
            value: type.uuid || type.id,
            label: type.name || type.title || 'Неизвестный тип'
        }));
        return items;
    };

    const getDepositTypesForCombobox = () => {
        let types: any = [];
        if (depositTypesData?.results) {
            types = depositTypesData.results;
        } else if (Array.isArray(depositTypesData)) {
            types = depositTypesData;
        } else if (depositTypesData?.data) {
            types = depositTypesData.data;
        }
        const items = types.map((type: any) => ({
            id: type.uuid || type.id,
            value: type.uuid || type.id,
            label: type.name || type.title || 'Неизвестный тип'
        }));
        return items;
    };

    const getReservoirTypesForCombobox = () => {
        const types = Array.isArray(reservoirTypesData) ? reservoirTypesData : reservoirTypesData?.results || [];
        const items = types.map((type: any) => ({
            id: type.uuid,
            value: type.uuid,
            label: type.name
        }));
        return items;
    };

    // Установка данных в локальное состояние при получении
    useEffect(() => {
        if (fieldUuidData && fieldModal.isOpen) {
            const companiesFromApi = fieldUuidData.companies?.map((company: any) => ({
                id: company.uuid,
                value: company.uuid,
                label: company.name
            })) || [];

            const fieldType = fieldUuidData.type ? {
                id: fieldUuidData.type,
                value: fieldUuidData.type,
                label: fieldUuidData.type_name || fieldUuidData.type
            } : null;

            setLocalField({
                ...fieldUuidData,
                name: fieldUuidData.name || '',
                description: fieldUuidData.description || '',
                district: fieldUuidData.geographic_region || '',
                fondMining: fieldUuidData.total_producing_wells?.toString() || '0',
                fondInjection: fieldUuidData.total_injection_wells?.toString() || '0',
                stage: fieldUuidData.current_stage?.toString(),
                type: fieldType,
                company: companiesFromApi
            });
        }
    }, [fieldUuidData, fieldModal.isOpen, setLocalField]);

    useEffect(() => {
        if (depositUuidData && depositModal.isOpen) {
            const companiesFromApi = depositUuidData.companies?.map((company: any) => ({
                id: company.uuid,
                value: company.uuid,
                label: company.name
            })) || [];

            const depositType = depositUuidData.type ? {
                id: depositUuidData.type,
                value: depositUuidData.type,
                label: depositUuidData.type_name || depositUuidData.type
            } : null;

            setLocalDeposit({
                ...depositUuidData,
                name: depositUuidData.name || '',
                description: depositUuidData.description || '',
                fondMining: depositUuidData.total_producing_wells?.toString() || '0',
                fondInjection: depositUuidData.total_injection_wells?.toString() || '0',
                type: depositType,
                company: companiesFromApi
            });
        }
    }, [depositUuidData, depositModal.isOpen, setLocalDeposit]);

    useEffect(() => {
        if (areaUuidData && sectionModal.isOpen) {
            let companiesFromApi: any = [];
            if (areaUuidData.companies && Array.isArray(areaUuidData.companies)) {
                companiesFromApi = areaUuidData.companies.map((company: any) => ({
                    id: company.uuid,
                    value: company.uuid,
                    label: company.name
                }));
            } else if (areaUuidData.company) {
                const companyUuid = areaUuidData.company;
                const company = companiesData?.results?.find(c => c.uuid === companyUuid);
                if (company) {
                    companiesFromApi = [{
                        id: company.uuid,
                        value: company.uuid,
                        label: company.name
                    }];
                }
            }

            setLocalSection({
                ...areaUuidData,
                name: areaUuidData.name || '',
                description: areaUuidData.description || '',
                fondMining: areaUuidData.total_producing_wells?.toString() || '0',
                fondInjection: areaUuidData.total_injection_wells?.toString() || '0',
                company: companiesFromApi
            });
        }
    }, [areaUuidData, sectionModal.isOpen, setLocalSection, companiesData]);

    useEffect(() => {
        if (horizonUuidData && horizonModal.isOpen) {
            setLocalHorizon({
                ...horizonUuidData,
                cipher: horizonUuidData.code || '',
                description: horizonUuidData.description || '',
                depth: horizonUuidData.burial_depth?.toString() || '0'
            });
        }
    }, [horizonUuidData, horizonModal.isOpen, setLocalHorizon]);

    useEffect(() => {
        if (layerUuidData && bedModal.isOpen) {
            const reservoirType = layerUuidData.reservoir ? {
                id: layerUuidData.reservoir,
                value: layerUuidData.reservoir,
                label: layerUuidData.reservoir_name || 'Неизвестный тип'
            } : null;

            setLocalBed({
                ...layerUuidData,
                name: layerUuidData.name || '',
                description: layerUuidData.description || '',
                depth: layerUuidData.burial_depth?.toString() || '0',
                porosity: layerUuidData.porosity?.toString() || '0',
                permeability: layerUuidData.permeability?.toString() || '0',
                thickness: layerUuidData.formation_thickness?.toString() || '0',
                reservoirType: reservoirType
            });
        }
    }, [layerUuidData, bedModal.isOpen, setLocalBed]);

    // Методы создания через API
    const handleCreateField = async () => {
        if (!localField) return;

        try {
            const fieldData: any = {
                name: localField.name,
                description: localField.description || '',
                geographic_region: localField.district || '',
                total_producing_wells: parseInt(localField.fondMining || '0'),
                total_injection_wells: parseInt(localField.fondInjection || '0'),
                type: localField.type?.value || '',
                current_stage: parseInt(localField.stage) !== 5 ? parseInt(localField.stage) : null,
                company_ids: localField.company ? localField.company.map((c: any) => c.value) : []
            };

            await postField(fieldData).unwrap();
            onSaveField();
        } catch (error) {
            console.error('Ошибка создания месторождения:', error);
        }
    };

    const handleUpdateField = async () => {
        if (!localField || !fieldModal.field?.uuid) return;

        try {
            const fieldData: any = {
                uuid: fieldModal.field.uuid,
                name: localField.name,
                description: localField.description || '',
                geographic_region: localField.district || '',
                total_producing_wells: parseInt(localField.fondMining || '0'),
                total_injection_wells: parseInt(localField.fondInjection || '0'),
                type: localField.type?.value || '',
                current_stage: parseInt(localField.stage) !== 5 ? parseInt(localField.stage) : null,
                company_ids: localField.company ? localField.company.map((c: any) => c.value) : []
            };

            await patchField(fieldData).unwrap();
            onSaveField();
        } catch (error) {
            console.error('Ошибка обновления месторождения:', error);
        }
    };

    const handleCreateDeposit = async () => {
        if (!localDeposit) return;

        try {
            const depositData = {
                name: localDeposit.name,
                description: localDeposit.description || '',
                total_producing_wells: parseInt(localDeposit.fondMining || '0'),
                total_injection_wells: parseInt(localDeposit.fondInjection || '0'),
                type: localDeposit.type?.value || '',
                field: depositModal.fieldId,
                company_ids: localDeposit.company ? localDeposit.company.map((c: any) => c.value) : []
            };

            await postDeposit(depositData).unwrap();
            onSaveDeposit();
        } catch (error) {
            console.error('Ошибка создания залежи:', error);
        }
    };

    const handleUpdateDeposit = async () => {
        if (!localDeposit || !depositModal.deposit?.uuid) return;

        try {
            const depositData = {
                uuid: depositModal.deposit.uuid,
                name: localDeposit.name,
                description: localDeposit.description || '',
                total_producing_wells: parseInt(localDeposit.fondMining || '0'),
                total_injection_wells: parseInt(localDeposit.fondInjection || '0'),
                type: localDeposit.type?.value || '',
                field: depositModal.fieldId,
                company_ids: localDeposit.company ? localDeposit.company.map((c: any) => c.value) : []
            };

            await patchDeposit(depositData).unwrap();
            onSaveDeposit();
        } catch (error) {
            console.error('Ошибка обновления залежи:', error);
        }
    };

    const handleCreateSection = async () => {
        if (!localSection) return;

        try {
            const sectionData: any = {
                name: localSection?.name,
                description: localSection.description || '',
                deposit: sectionModal.depositId,
                company: localSection.company && localSection.company.length > 0 ? localSection.company[0].value : '',
                total_producing_wells: parseInt(localSection.fondMining || '0'),
                total_injection_wells: parseInt(localSection.fondInjection || '0')
            };

            await postArea(sectionData).unwrap();
            onSaveSection();
        } catch (error) {
            console.error('Ошибка создания участка:', error);
        }
    };

    const handleUpdateSection = async () => {
        if (!localSection || !sectionModal.section?.uuid) return;

        try {
            const sectionData: any = {
                uuid: sectionModal.section.uuid,
                name: localSection?.name,
                description: localSection.description || '',
                deposit: sectionModal.depositId,
                company: localSection.company && localSection.company.length > 0 ? localSection.company[0].value : '',
                total_producing_wells: parseInt(localSection.fondMining || '0'),
                total_injection_wells: parseInt(localSection.fondInjection || '0')
            };

            await patchArea(sectionData).unwrap();
            onSaveSection();
        } catch (error) {
            console.error('Ошибка обновления участка:', error);
        }
    };

    const handleCreateHorizon = async () => {
        if (!localHorizon) return;

        try {
            const horizonData = {
                code: localHorizon.cipher || '',
                name: localHorizon.name || '',
                description: localHorizon.description || '',
                burial_depth: parseFloat(localHorizon.depth || '0'),
                area: horizonModal.sectionId
            };

            await postHorizon(horizonData).unwrap();
            onSaveHorizon();
        } catch (error) {
            console.error('Ошибка создания горизонта:', error);
        }
    };

    const handleUpdateHorizon = async () => {
        if (!localHorizon || !horizonModal.horizon?.uuid) return;

        try {
            const horizonData = {
                uuid: horizonModal.horizon.uuid,
                code: localHorizon.cipher || '',
                name: localHorizon.name || '',
                description: localHorizon.description || '',
                burial_depth: parseFloat(localHorizon.depth || '0'),
                area: horizonModal.sectionId
            };

            await patchHorizon(horizonData).unwrap();
            onSaveHorizon();
        } catch (error) {
            console.error('Ошибка обновления горизонта:', error);
        }
    };

    const handleCreateBed = async () => {
        if (!localBed) return;

        try {
            const layerData = {
                name: localBed.name,
                description: localBed.description || '',
                burial_depth: parseFloat(localBed.depth || '0'),
                porosity: parseFloat(localBed.porosity || '0'),
                permeability: parseFloat(localBed.permeability || '0'),
                formation_thickness: parseFloat(localBed.thickness || '0'),
                horizon: bedModal.horizonId,
                reservoir: localBed.reservoirType?.value || ''
            };

            await postLayer(layerData).unwrap();
            onSaveBed();
        } catch (error) {
            console.error('Ошибка создания пласта:', error);
        }
    };

    const handleUpdateBed = async () => {
        if (!localBed || !bedModal.bed?.uuid) return;

        try {
            const layerData = {
                uuid: bedModal.bed.uuid,
                name: localBed.name,
                description: localBed.description || '',
                burial_depth: parseFloat(localBed.depth || '0'),
                porosity: parseFloat(localBed.porosity || '0'),
                permeability: parseFloat(localBed.permeability || '0'),
                formation_thickness: parseFloat(localBed.thickness || '0'),
                horizon: bedModal.horizonId,
                reservoir: localBed.reservoirType?.value || ''
            };

            await patchLayer(layerData).unwrap();
            onSaveBed();
        } catch (error) {
            console.error('Ошибка обновления пласта:', error);
        }
    };

    // Обработчики сохранения (создание или редактирование)
    const handleSaveField = () => {
        if (fieldModal.field) {
            handleUpdateField();
        } else {
            handleCreateField();
        }
    };

    const handleSaveDeposit = () => {
        if (depositModal.deposit) {
            handleUpdateDeposit();
        } else {
            handleCreateDeposit();
        }
    };

    const handleSaveSection = () => {
        if (sectionModal.section) {
            handleUpdateSection();
        } else {
            handleCreateSection();
        }
    };

    const handleSaveHorizon = () => {
        if (horizonModal.horizon) {
            handleUpdateHorizon();
        } else {
            handleCreateHorizon();
        }
    };
    console.log(bedModal.bed)

    const handleSaveBed = () => {
        if (bedModal.bed) {
            handleUpdateBed();
        } else {
            handleCreateBed();
        }
    };

    // Сброс данных при закрытии модальных окон
    const handleCloseFieldModal = () => {
        setFieldModal({ isOpen: false, field: null });
        setLocalField(null);
    };

    const handleCloseDepositModal = () => {
        setDepositModal({ isOpen: false, fieldId: '', deposit: null });
        setLocalDeposit(null);
    };

    const handleCloseSectionModal = () => {
        setSectionModal({ isOpen: false, fieldId: '', depositId: '', section: null });
        setLocalSection(null);
    };

    const handleCloseHorizonModal = () => {
        setHorizonModal({ isOpen: false, fieldId: '', depositId: '', sectionId: '', horizon: null });
        setLocalHorizon(null);
    };

    const handleCloseBedModal = () => {
        setBedModal({ isOpen: false, fieldId: '', depositId: '', sectionId: '', horizonId: '', bed: null });
        setLocalBed(null);
    };

    console.log(localHorizon)

    return (
        <>
            {/* Модальное окно для месторождения */}
            <Modal isOpen={fieldModal.isOpen} onClose={handleCloseFieldModal}>
                <div style={{ padding: 20, minWidth: 500 }} className={classes.modal}>
                    <h3>{fieldModal.field ? 'Редактировать' : 'Создать'} месторождение</h3>
                    <div className={classes.modalContainer}>
                        <div className={classes.modalContent}>
                            <div className={classes.modalName}>
                                <Combobox
                                    label="Название"
                                    value={localField?.name ? { id: localField.name, label: localField.name } : null}
                                    items={fieldNames.map((item: any) => ({
                                        id: item.value,
                                        label: item.value
                                    }))}
                                    onChange={(value) => setLocalField((prev: any) => prev ? { ...prev, name: value?.label || '' } : null)}
                                    placeholder="Выберите название"
                                    disabled={isLoadingFieldUuid}
                                    isLoading={isLoadingLookups}
                                    getItemLabel={(item) => item.label}
                                    getItemKey={(item) => item.id}
                                />
                                <Combobox
                                    label="Компании"
                                    value={localField?.company || []}
                                    items={getCompaniesForCombobox()}
                                    multiple
                                    onChange={(value) => {
                                        setLocalField((prev: any) => prev ? { ...prev, company: value || [] } : null);
                                    }}
                                    isLoading={isLoadingCompanies || isLoadingFieldUuid}
                                    disabled={isLoadingFieldUuid}
                                    getItemLabel={(item) => item.label}
                                    getItemKey={(item) => item.value}
                                />
                            </div>
                            <div className={classes.modalType}>
                                <Combobox
                                    label="Тип"
                                    value={localField?.type}
                                    items={getFieldTypesForCombobox()}
                                    onChange={(value) => {
                                        setLocalField((prev: any) => prev ? { ...prev, type: value } : null);
                                    }}
                                    isLoading={isLoadingFieldTypes || isLoadingFieldUuid}
                                    disabled={isLoadingFieldUuid}
                                    getItemLabel={(item: any) => item.label}
                                    getItemKey={(item: any) => item.value}
                                />
                                <Combobox
                                    label="Стадия"
                                    items={itemsStageCombobox}
                                    value={itemsStageCombobox.find((item: any) => item.id === parseInt(localField?.stage)) || null}
                                    onChange={(value) => setLocalField((prev: any) => prev ? { ...prev, stage: value ? value.id.toString() : null } : null)}
                                    disabled={isLoadingFieldUuid}
                                    placeholder='Выберите стадию'
                                    getItemLabel={(item) => item.label.toString()}
                                    getItemKey={(item) => item.id}
                                />
                            </div>
                            <TextField
                                label="Географический район"
                                value={localField?.district || ''}
                                onChange={(value) => setLocalField((prev: any) => prev ? { ...prev, district: value || '' } : null)}
                                disabled={isLoadingFieldUuid}
                            />
                            <div className={classes.modalFond}>
                                <TextField
                                    label="Фонды добывающих скважин"
                                    value={localField?.fondMining || ''}
                                    onChange={(value) => setLocalField((prev: any) => prev ? { ...prev, fondMining: value || '' } : null)}
                                    disabled={isLoadingFieldUuid}
                                />
                                <TextField
                                    label="Фонды нагнетательных скважин"
                                    value={localField?.fondInjection || ''}
                                    onChange={(value) => setLocalField((prev: any) => prev ? { ...prev, fondInjection: value || '' } : null)}
                                    disabled={isLoadingFieldUuid}
                                />
                            </div>
                        </div>
                        <TextField
                            type="textarea"
                            rows={3}
                            label='Описание'
                            placeholder="Описание"
                            value={localField?.description || ''}
                            onChange={(value) => setLocalField((prev: any) => prev ? { ...prev, description: value || '' } : null)}
                            disabled={isLoadingFieldUuid}
                        />
                    </div>
                    <div className={classes.buttons}>
                        <Button label="Отмена" view="secondary" onClick={handleCloseFieldModal} />
                        <Button
                            label={fieldModal.field ? "Сохранить" : "Создать"}
                            onClick={handleSaveField}
                            loading={isCreatingField || isUpdatingField || isLoadingFieldUuid}
                            disabled={isCreatingField || isUpdatingField || isLoadingFieldUuid || !localField?.name}
                        />
                    </div>
                </div>
            </Modal>

            {/* Модальное окно для залежи */}
            <Modal isOpen={depositModal.isOpen} onClose={handleCloseDepositModal}>
                <div style={{ padding: 20, minWidth: 500 }} className={classes.modal}>
                    <h3>{depositModal.deposit ? 'Редактировать' : 'Создать'} залежь</h3>
                    <div style={{ display: 'flex', gap: 20 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <Combobox
                                label="Название залежи"
                                value={localDeposit?.name ? { id: localDeposit.name, label: localDeposit.name } : null}
                                items={depositNames.map((item: any) => ({
                                    id: item.value,
                                    label: item.value
                                }))}
                                onChange={(value) => setLocalDeposit((prev: any) => prev ? { ...prev, name: value?.label || '' } : null)}
                                placeholder="Выберите название"
                                style={{ width: '31vw' }}
                                disabled={isLoadingDepositUuid}
                                isLoading={isLoadingLookups}
                                getItemLabel={(item) => item.label}
                                getItemKey={(item) => item.id}
                            />
                            <Combobox
                                label="Компании"
                                items={getCompaniesForCombobox()}
                                value={localDeposit?.company || []}
                                multiple
                                onChange={(value) => {
                                    setLocalDeposit((prev: any) => prev ? { ...prev, company: value || [] } : null);
                                }}
                                style={{ width: '31vw' }}
                                isLoading={isLoadingCompanies || isLoadingDepositUuid}
                                disabled={isLoadingDepositUuid}
                                getItemLabel={(item) => item.label}
                                getItemKey={(item) => item.value}
                            />
                            <Combobox
                                label="Тип"
                                items={getDepositTypesForCombobox()}
                                value={localDeposit?.type}
                                onChange={(value) => {
                                    setLocalDeposit((prev: any) => prev ? { ...prev, type: value } : null);
                                }}
                                style={{ width: '31vw' }}
                                isLoading={isLoadingDepositTypes || isLoadingDepositUuid}
                                disabled={isLoadingDepositUuid}
                                getItemLabel={(item: any) => item.label}
                                getItemKey={(item: any) => item.value}
                            />
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                <TextField
                                    label="Фонд добывающих скважин"
                                    value={localDeposit?.fondMining || ''}
                                    onChange={(value) => setLocalDeposit((prev: any) => prev ? { ...prev, fondMining: value || '' } : null)}
                                    style={{ width: '15vw' }}
                                    disabled={isLoadingDepositUuid}
                                />
                                <TextField
                                    label="Фонд нагнетательных скважин"
                                    value={localDeposit?.fondInjection || ''}
                                    onChange={(value) => setLocalDeposit((prev: any) => prev ? { ...prev, fondInjection: value || '' } : null)}
                                    style={{ width: '15vw' }}
                                    disabled={isLoadingDepositUuid}
                                />
                            </div>
                        </div>
                        <div style={{ width: '40vw', paddingBottom: '20px' }}>
                            <TextField
                                type="textarea"
                                rows={13}
                                label='Описание'
                                placeholder="Описание"
                                value={localDeposit?.description || ''}
                                onChange={(value) => setLocalDeposit((prev: any) => prev ? { ...prev, description: value || '' } : null)}
                                disabled={isLoadingDepositUuid}
                            />
                        </div>
                    </div>
                    <div className={classes.buttons}>
                        <Button label="Отмена" view="secondary" onClick={handleCloseDepositModal} />
                        <Button
                            label={depositModal.deposit ? "Сохранить" : "Создать"}
                            onClick={handleSaveDeposit}
                            loading={isCreatingDeposit || isUpdatingDeposit || isLoadingDepositUuid}
                            disabled={isCreatingDeposit || isUpdatingDeposit || isLoadingDepositUuid || !localDeposit?.name}
                        />
                    </div>
                </div>
            </Modal>

            {/* Модальное окно для участка */}
            <Modal isOpen={sectionModal.isOpen} onClose={handleCloseSectionModal}>
                <div style={{ padding: 20, minWidth: 500 }} className={classes.modal}>
                    <h3>{sectionModal.section ? 'Редактировать' : 'Создать'} участок</h3>
                    <div style={{ display: 'flex', gap: 20 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            <TextField
                                label='Название участка'
                                placeholder="Выберите название"
                                style={{ width: '31vw' }}
                                value={localSection?.name}
                                onChange={(value) => setLocalSection((prev: any) => prev ? { ...prev, name: value || '' } : null)}
                            />
                            {/* <Combobox
                                label="Название участка"
                                value={localSection?.name ? { id: localSection.name, label: localSection.name } : null}
                                items={areaNames.map((item: any) => ({
                                    id: item.value,
                                    label: item.value
                                }))}
                                onChange={(value) => setLocalSection((prev: any) => prev ? { ...prev, name: value?.label || '' } : null)}
                                placeholder="Выберите название"
                                style={{ width: '31vw' }}
                                disabled={isLoadingAreaUuid}
                                isLoading={isLoadingLookups}
                                getItemLabel={(item) => item.label}
                                getItemKey={(item) => item.id}
                            /> */}
                            <Combobox
                                label="Компании"
                                items={getCompaniesForCombobox()}
                                value={localSection?.company && localSection.company.length > 0 ? localSection.company[0] : null}
                                multiple={false}
                                onChange={(value) => {
                                    setLocalSection((prev: any) => prev ? {
                                        ...prev,
                                        company: value ? [value] : []
                                    } : null);
                                }}
                                style={{ width: '31vw' }}
                                isLoading={isLoadingCompanies || isLoadingAreaUuid}
                                disabled={isLoadingAreaUuid}
                                getItemLabel={(item) => item.label}
                                getItemKey={(item) => item.value}
                            />
                            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                                <TextField
                                    label="Фонд добывающих скважин"
                                    value={localSection?.fondMining || ''}
                                    onChange={(value) => setLocalSection((prev: any) => prev ? { ...prev, fondMining: value || '' } : null)}
                                    style={{ width: '15vw' }}
                                    disabled={isLoadingAreaUuid}
                                />
                                <TextField
                                    label="Фонд нагнетательных скважин"
                                    value={localSection?.fondInjection || ''}
                                    onChange={(value) => setLocalSection((prev: any) => prev ? { ...prev, fondInjection: value || '' } : null)}
                                    style={{ width: '15vw' }}
                                    disabled={isLoadingAreaUuid}
                                />
                            </div>
                        </div>
                        <div style={{ width: '40vw' }}>
                            <TextField
                                type="textarea"
                                rows={9}
                                label='Описание'
                                placeholder="Описание"
                                value={localSection?.description || ''}
                                onChange={(value) => setLocalSection((prev: any) => prev ? { ...prev, description: value || '' } : null)}
                                disabled={isLoadingAreaUuid}
                            />
                        </div>
                    </div>
                    <div className={classes.buttons}>
                        <Button label="Отмена" view="secondary" onClick={handleCloseSectionModal} />
                        <Button
                            label={sectionModal.section ? "Сохранить" : "Создать"}
                            onClick={handleSaveSection}
                            loading={isCreatingArea || isUpdatingArea || isLoadingAreaUuid}
                            disabled={isCreatingArea || isUpdatingArea || isLoadingAreaUuid || !localSection?.name}
                        />
                    </div>
                </div>
            </Modal>

            {/* Модальное окно для горизонта */}
            <Modal isOpen={horizonModal.isOpen} onClose={handleCloseHorizonModal}>
                <div style={{ padding: 20, minWidth: 500 }} className={classes.modal}>
                    <h3>{horizonModal.horizon ? 'Редактировать' : 'Создать'} горизонт</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ display: 'flex', gap: 16 }}>
                            <Combobox
                                label="Назавние горизонта"
                                value={localHorizon?.name ? { id: localHorizon.name, label: localHorizon.name } : null}
                                items={horizonCodes.map((item: any) => ({
                                    id: item.value,
                                    label: item.value
                                }))}
                                onChange={(value) => setLocalHorizon((prev: any) => prev ? { ...prev, name: value?.label || '' } : null)}
                                placeholder="Выберите горизонт"
                                style={{ flex: 1 }}
                                disabled={isLoadingHorizonUuid}
                                isLoading={isLoadingLookups}
                                getItemLabel={(item) => item.label}
                                getItemKey={(item) => item.id}
                            />
                            <Combobox
                                label="Шифр"
                                value={localHorizon?.cipher ? { id: localHorizon.cipher, label: localHorizon.cipher } : null}
                                items={codesHorizon.map((item: any) => ({
                                    id: item.value,
                                    label: item.value
                                }))}
                                onChange={(value) => setLocalHorizon((prev: any) => prev ? { ...prev, cipher: value?.label || '' } : null)}
                                placeholder="Выберите шифр"
                                style={{ flex: 1 }}
                                disabled={isLoadingHorizonUuid}
                                isLoading={isLoadingLookups}
                                getItemLabel={(item) => item.label}
                                getItemKey={(item) => item.id}
                            />
                        </div>
                        <TextField
                            label="Глубина залегания"
                            value={localHorizon?.depth || ''}
                            onChange={(value) => setLocalHorizon((prev: any) => prev ? { ...prev, depth: value || '' } : null)}
                            placeholder="Глубина залегания"
                            style={{ flex: 1 }}
                            disabled={isLoadingHorizonUuid}
                        />
                        <TextField
                            type="textarea"
                            rows={4}
                            label='Описание'
                            placeholder="Описание горизонта"
                            value={localHorizon?.description || ''}
                            onChange={(value) => setLocalHorizon((prev: any) => prev ? { ...prev, description: value || '' } : null)}
                            style={{ paddingBottom: '20px' }}
                            disabled={isLoadingHorizonUuid}
                        />
                    </div>
                    <div className={classes.buttons}>
                        <Button label="Отмена" view="secondary" onClick={handleCloseHorizonModal} />
                        <Button
                            label={horizonModal.horizon ? "Сохранить" : "Создать"}
                            onClick={handleSaveHorizon}
                            loading={isCreatingHorizon || isUpdatingHorizon || isLoadingHorizonUuid}
                            disabled={isCreatingHorizon || isUpdatingHorizon || isLoadingHorizonUuid || !localHorizon?.name}
                        />
                    </div>
                </div>
            </Modal>

            {/* Модальное окно для пласта */}
            <Modal isOpen={bedModal.isOpen} onClose={handleCloseBedModal}>
                <div style={{ padding: 20, minWidth: 500 }} className={classes.modal}>
                    <h3>{bedModal.bed ? 'Редактировать' : 'Создать'} пласт</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ display: 'flex', gap: 16 }}>
                            <Combobox
                                label="Имя"
                                value={localBed?.name ? { id: localBed.name, label: localBed.name } : null}
                                items={layerNames?.map((item: any) => ({
                                    id: item.value,
                                    label: item.value
                                }))}
                                onChange={(value) => setLocalBed((prev: any) => prev ? { ...prev, name: value?.label || '' } : null)}
                                placeholder="Выберите имя"
                                style={{ flex: 1 }}
                                disabled={isLoadingLayerUuid}
                                isLoading={isLoadingLookups}
                                getItemLabel={(item) => item.label}
                                getItemKey={(item) => item.id}
                            />
                            <TextField
                                label="Глубина залегания"
                                value={localBed?.depth || ''}
                                onChange={(value) => setLocalBed((prev: any) => prev ? { ...prev, depth: value || '' } : null)}
                                placeholder="Глубина залегания"
                                style={{ flex: 1 }}
                                disabled={isLoadingLayerUuid}
                            />
                        </div>
                        <TextField
                            type="textarea"
                            rows={3}
                            label='Описание'
                            placeholder="Описание пласта"
                            value={localBed?.description || ''}
                            onChange={(value) => setLocalBed((prev: any) => prev ? { ...prev, description: value || '' } : null)}
                            disabled={isLoadingLayerUuid}
                        />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <TextField
                                label="Пористость"
                                value={localBed?.porosity || ''}
                                onChange={(value) => setLocalBed((prev: any) => prev ? { ...prev, porosity: value || '' } : null)}
                                placeholder="Пористость"
                                disabled={isLoadingLayerUuid}
                            />
                            <TextField
                                label="Проницаемость"
                                value={localBed?.permeability || ''}
                                onChange={(value) => setLocalBed((prev: any) => prev ? { ...prev, permeability: value || '' } : null)}
                                placeholder="Проницаемость"
                                disabled={isLoadingLayerUuid}
                            />
                            <TextField
                                label="Толщина пласта"
                                value={localBed?.thickness || ''}
                                onChange={(value) => setLocalBed((prev: any) => prev ? { ...prev, thickness: value || '' } : null)}
                                placeholder="Толщина пласта"
                                disabled={isLoadingLayerUuid}
                            />
                            <Combobox
                                label="Тип коллектора"
                                items={getReservoirTypesForCombobox()}
                                value={localBed?.reservoirType}
                                onChange={(value) => {
                                    setLocalBed((prev: any) => prev ? { ...prev, reservoirType: value } : null);
                                }}
                                style={{ paddingBottom: '20px' }}
                                isLoading={isLoadingReservoirTypes || isLoadingLayerUuid}
                                disabled={isLoadingLayerUuid}
                                getItemLabel={(item) => item.label}
                                getItemKey={(item) => item.value}
                            />
                        </div>
                    </div>
                    <div className={classes.buttons}>
                        <Button label="Отмена" view="secondary" onClick={handleCloseBedModal} />
                        <Button
                            label={bedModal.bed ? "Сохранить" : "Создать"}
                            onClick={handleSaveBed}
                            loading={isCreatingLayer || isUpdatingLayer || isLoadingLayerUuid}
                            disabled={isCreatingLayer || isUpdatingLayer || isLoadingLayerUuid || !localBed?.name}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default FieldModals;